import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  DocumentTextIcon, 
  TrashIcon, 
  XMarkIcon,
  BriefcaseIcon,
  ChevronRightIcon,
  DocumentMagnifyingGlassIcon,
  DocumentPlusIcon,
} from '@heroicons/react/24/outline'
import { 
  fetchUserAnalyses, 
  fetchLatestResume, 
  deleteResume, 
  fetchUserComparisons,
  compareResumeToApplication 
} from '../services/resumeService'
import { fetchUserSettings } from '../services/settingsRepo'
import { useApplications } from '../hooks/useApplications'
import { ResumeAnalyzer } from './ResumeAnalyzer'
import { UpgradePrompt, ProBadge } from './UpgradePrompt'
import { Skeleton } from './Skeleton'
import { PageLayout } from './PageLayout'
import type { ResumeAnalysis, ResumeComparisonRecord } from '../types'

const ANALYZING_MESSAGES = [
  'Analyzing your resume...',
  'Comparing skills...',
  'Checking experience match...',
  'Evaluating qualifications...',
  'Identifying gaps...',
  'Finding strengths...',
  'Preparing suggestions...',
  'Almost done...',
]

interface Props {
  userId: string
  plan: 'free' | 'pro'
}

type HistoryItem = 
  | { type: 'analysis'; data: ResumeAnalysis }
  | { type: 'comparison'; data: ResumeComparisonRecord }

export function ResumePage({ userId, plan }: Props) {
  const navigate = useNavigate()
  const { applications } = useApplications()
  
  const [analyses, setAnalyses] = useState<ResumeAnalysis[]>([])
  const [comparisons, setComparisons] = useState<ResumeComparisonRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [currentResume, setCurrentResume] = useState<ResumeAnalysis | null>(null)
  const [hasConsent, setHasConsent] = useState(false)
  
  // Delete modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  
  // Compare with application
  const [selectedAppId, setSelectedAppId] = useState<string>('')
  const [isComparing, setIsComparing] = useState(false)
  const [compareError, setCompareError] = useState<string | null>(null)
  const [messageIndex, setMessageIndex] = useState(0)

  // Fetch all data
  useEffect(() => {
    if (userId) {
      Promise.all([
        fetchUserAnalyses(userId),
        fetchLatestResume(userId),
        fetchUserSettings(userId),
        fetchUserComparisons(userId)
      ])
        .then(([analysesData, latestResume, settings, comparisonsData]) => {
          setAnalyses(analysesData)
          setCurrentResume(latestResume)
          setHasConsent(settings.resumeConsentGiven)
          setComparisons(comparisonsData)
        })
        .catch((error) => {
          console.error('Failed to fetch data:', error)
        })
        .finally(() => setLoading(false))
    }
  }, [userId])

  // Rotate analyzing messages
  useEffect(() => {
    if (!isComparing) {
      setMessageIndex(0)
      return
    }
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % ANALYZING_MESSAGES.length)
    }, 2500)
    return () => clearInterval(interval)
  }, [isComparing])

  // Check if user can analyze - Free users get 1 analysis total, Pro unlimited
  const canAnalyze = plan === 'pro' || analyses.length === 0

  const handleAnalysisComplete = (analysis: ResumeAnalysis | null) => {
    if (analysis) {
      setCurrentResume(analysis)
      setAnalyses((prev) => [analysis, ...prev])
      navigate(`/resume/${analysis.id}`)
    }
  }

  const handleDeleteResume = async () => {
    if (!currentResume?.file_path) return
    
    setIsDeleting(true)
    try {
      await deleteResume(currentResume.file_path, userId)
      setCurrentResume(null)
      // Don't remove from analyses - keeps in history with cleared file
      setIsDeleteModalOpen(false)
    } catch (err) {
      console.error('Failed to delete resume:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCompareWithApplication = async () => {
    if (!selectedAppId || !currentResume) return
    
    setIsComparing(true)
    setCompareError(null)
    
    try {
      const result = await compareResumeToApplication(selectedAppId)
      // If we got an ID back, navigate to the comparison page
      if (result.id) {
        setComparisons((prev) => [{
          id: result.id!,
          user_id: userId,
          application_id: result.application_id,
          resume_analysis_id: currentResume.id,
          match_score: result.match_score,
          matched_skills: result.matched_skills,
          missing_skills: result.missing_skills,
          experience_match_score: result.experience_match?.score ?? null,
          experience_match_feedback: result.experience_match?.feedback ?? null,
          strengths_for_role: result.strengths_for_role,
          gaps_for_role: result.gaps_for_role,
          suggestions: result.suggestions,
          interview_tips: result.interview_tips,
          overall_assessment: result.overall_assessment,
          company: result.company,
          position_title: result.position ?? null,
          resume_file_name: result.resume_file,
          created_at: result.analyzed_at,
          updated_at: result.analyzed_at,
        }, ...prev])
        navigate(`/resume/comparison/${result.id}`)
      }
    } catch (err) {
      setCompareError(err instanceof Error ? err.message : 'Comparison failed')
    } finally {
      setIsComparing(false)
    }
  }

  // Combine and sort history
  const historyItems: HistoryItem[] = [
    ...analyses.map((a) => ({ type: 'analysis' as const, data: a })),
    ...comparisons.map((c) => ({ type: 'comparison' as const, data: c })),
  ].sort((a, b) => {
    const dateA = new Date(a.data.created_at).getTime()
    const dateB = new Date(b.data.created_at).getTime()
    return dateB - dateA
  })

  // Get applications that have job content for dropdown
  const applicationsWithContent = applications.filter((app) => app.rawMarkdown)

  // Score color helper
  const getScoreColor = (score: number) => {
    if (score >= 75) return 'text-emerald-600 dark:text-emerald-400'
    if (score >= 50) return 'text-amber-600 dark:text-amber-400'
    return 'text-rose-600 dark:text-rose-400'
  }

  // Skeleton components
  const CurrentResumeSkeleton = () => (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
        <Skeleton className="h-9 w-20 rounded-lg" />
      </div>
    </div>
  )

  const CompareSkeleton = () => (
    <div className="card p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-5 rounded" />
        <Skeleton className="h-5 w-48" />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Skeleton className="flex-1 h-10 rounded-lg" />
        <Skeleton className="h-10 w-24 rounded-lg" />
      </div>
    </div>
  )

  const HistorySkeleton = () => (
    <div className="space-y-3">
      <Skeleton className="h-6 w-40" />
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card p-4 flex items-center gap-4">
            <Skeleton className="h-10 w-16 rounded" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <PageLayout
      icon={DocumentTextIcon}
      title="Resume Analysis"
      subtitle="AI-powered feedback to improve your resume"
    >
      {/* Upload / Analyze Section */}
      <div className="card p-6 space-y-6">
        {canAnalyze ? (
          <ResumeAnalyzer
            userId={userId}
            onAnalysisComplete={handleAnalysisComplete}
            currentAnalysis={null}
            canAnalyzeNew={canAnalyze}
            hasConsent={hasConsent}
            onConsentGiven={() => setHasConsent(true)}
          />
        ) : (
          <UpgradePrompt type="feature" featureName="Resume Analysis" />
        )}
      </div>

      {/* Current Resume Section */}
      {loading ? (
        <CurrentResumeSkeleton />
      ) : currentResume ? (
        <div className="card p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-950/30">
                <DocumentTextIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase text-app-muted">Current Resume</p>
                <p className="font-medium text-app-ink truncate">{currentResume.file_name}</p>
                <p className="text-xs text-app-muted">
                  Uploaded {new Date(currentResume.created_at).toLocaleDateString('pl-PL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="flex-shrink-0 flex items-center gap-1.5 rounded-lg border border-rose-300 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-sm font-medium text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/30 transition-colors"
            >
              <TrashIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="card p-4 opacity-60">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-app-page">
              <DocumentTextIcon className="h-5 w-5 text-app-muted" />
            </div>
            <div>
              <p className="text-xs uppercase text-app-muted">Current Resume</p>
              <p className="text-sm text-app-muted">Upload your resume above to see it here</p>
            </div>
          </div>
        </div>
      )}

      {/* Compare with Application */}
      {loading ? (
        <CompareSkeleton />
      ) : plan !== 'pro' ? (
        <div className="card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <DocumentMagnifyingGlassIcon className="h-5 w-5 text-app-muted" />
            <h2 className="font-semibold text-app-muted font-display">Compare with Job Application</h2>
            <ProBadge onClick={() => navigate('/pricing')} source="resume_page" feature="resume_match" />
          </div>
          <p className="text-sm text-app-muted">
            Compare your resume against specific job applications and get personalized tips.
          </p>
        </div>
      ) : currentResume && applicationsWithContent.length > 0 ? (
        <div className="card p-4 space-y-4">
          <div className="flex items-center gap-2">
            <DocumentMagnifyingGlassIcon className="h-5 w-5 text-sky-500" />
            <h2 className="font-semibold text-app-ink font-display">Compare with Job Application</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={selectedAppId}
              onChange={(e) => {
                setSelectedAppId(e.target.value)
                setCompareError(null)
              }}
              disabled={isComparing}
              className="flex-1 rounded-lg border border-app-line bg-app-surface px-3 py-2 text-sm text-app-ink focus:border-app-accent focus:outline-none disabled:opacity-50"
            >
              <option value="">Select an application...</option>
              {applicationsWithContent.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.company}{app.positionTitle ? ` — ${app.positionTitle}` : ''}
                </option>
              ))}
            </select>
            <button
              onClick={handleCompareWithApplication}
              disabled={!selectedAppId || isComparing}
              className="flex items-center justify-center gap-2 rounded-lg bg-app-primary px-4 py-2 text-sm font-medium text-white hover:bg-app-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[200px]"
            >
              {isComparing ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  {ANALYZING_MESSAGES[messageIndex]}
                </>
              ) : (
                <>
                  <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                  Compare
                </>
              )}
            </button>
          </div>
          {compareError && (
            <p className="text-sm text-rose-600 dark:text-rose-400">{compareError}</p>
          )}
        </div>
      ) : (
        <div className="card p-4 space-y-4 opacity-60">
          <div className="flex items-center gap-2">
            <DocumentMagnifyingGlassIcon className="h-5 w-5 text-app-muted" />
            <h2 className="font-semibold text-app-muted font-display">Compare with Job Application</h2>
          </div>
          <p className="text-sm text-app-muted">
            {!currentResume
              ? 'Upload your resume first to compare it with job applications'
              : 'Add job applications with content to enable comparison'}
          </p>
        </div>
      )}

      {/* History */}
      {loading ? (
        <HistorySkeleton />
      ) : historyItems.length > 0 ? (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-app-ink font-display">History</h2>
          <div className="space-y-3">
            {historyItems.slice(0, 15).map((item) => {
              if (item.type === 'analysis') {
                const analysis = item.data as ResumeAnalysis
                return (
                  <Link
                    key={`analysis-${analysis.id}`}
                    to={`/resume/${analysis.id}`}
                    className="card p-4 flex items-center gap-4 hover:border-app-line transition-colors group"
                  >
                    {/* Score */}
                    <div className={`text-2xl font-bold ${getScoreColor(analysis.overall_score)} w-16 text-center flex-shrink-0`}>
                      {analysis.overall_score}%
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-app-page text-app-muted">
                          General Analysis
                        </span>
                      </div>
                      <p className="text-sm font-medium text-app-ink truncate mt-1">
                        {analysis.file_name}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs text-app-muted">
                          ATS: {analysis.ats_score}%
                        </span>
                        <span className="text-xs text-app-muted">
                          {new Date(analysis.created_at).toLocaleDateString('pl-PL', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>

                    {/* Arrow */}
                    <ChevronRightIcon className="h-5 w-5 text-app-muted group-hover:text-app-muted transition-colors flex-shrink-0" />
                  </Link>
                )
              } else {
                const comparison = item.data as ResumeComparisonRecord
                const builderSuffix = ' (Resume Builder)'
                const isFromBuilder = comparison.resume_file_name?.endsWith(builderSuffix) ?? false
                const builderResumeTitle = isFromBuilder
                  ? comparison.resume_file_name!.slice(0, -builderSuffix.length)
                  : null
                return (
                  <Link
                    key={`comparison-${comparison.id}`}
                    to={`/resume/comparison/${comparison.id}`}
                    className="card p-4 flex items-center gap-4 hover:border-app-line transition-colors group"
                  >
                    {/* Score */}
                    <div className={`text-2xl font-bold ${getScoreColor(comparison.match_score)} w-16 text-center flex-shrink-0`}>
                      {comparison.match_score}%
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300">
                          Job Match
                        </span>
                        {isFromBuilder && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
                            <DocumentPlusIcon className="h-3 w-3" />
                            Resume Builder
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-app-ink truncate mt-1 flex items-center gap-1.5">
                        <BriefcaseIcon className="h-4 w-4 text-app-muted" />
                        {comparison.company}
                        {comparison.position_title && (
                          <span className="text-app-muted font-normal">
                            — {comparison.position_title}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-app-muted mt-1 truncate">
                        {isFromBuilder && builderResumeTitle && (
                          <span className="text-app-muted">{builderResumeTitle} • </span>
                        )}
                        {new Date(comparison.created_at).toLocaleDateString('pl-PL', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>

                    {/* Arrow */}
                    <ChevronRightIcon className="h-5 w-5 text-app-muted group-hover:text-app-muted transition-colors flex-shrink-0" />
                  </Link>
                )
              }
            })}
          </div>
        </div>
      ) : null}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && currentResume && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold text-rose-500 flex items-center gap-2">
                <TrashIcon className="h-5 w-5" /> Delete Resume
              </p>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="p-1 text-app-muted hover:bg-app-subtle rounded-lg"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-app-ink">
                Are you sure you want to delete this resume and its analysis? This action cannot be undone.
              </p>
              <p className="text-sm text-app-muted">
                File: <strong>{currentResume.file_name}</strong>
              </p>
              <div className="rounded-lg border border-amber-300 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-3">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Note:</strong> If you delete this resume, you will need to upload a new one before using the Resume Match feature.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteResume}
                disabled={isDeleting}
                className="flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {isDeleting && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                )}
                Delete Resume
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}
